import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';

// 租户状态枚举
export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

// 租户类型枚举
export enum TenantType {
  FREE = 'free',
  PRO = 'pro',
  ENTERPRISE = 'enterprise',
}

// 租户数据类型
export interface Tenant {
  id: string;
  name: string;
  slug: string; // 租户标识
  plan: TenantType; // 租户类型
  status: TenantStatus;
  member_count: number;
  description: string;
  created_at: string;
  updated_at?: string;
}

// 租户成员类型
export interface TenantMember {
  id: string;
  username: string;
  email: string;
  role: string;
  status: string;
}

// 创建租户请求参数
export interface CreateTenantRequest {
  name: string;
  slug: string; // 租户标识
  plan: TenantType; // 租户类型
  status: TenantStatus;
  description?: string;
}

// 更新租户请求参数
export interface UpdateTenantRequest {
  name?: string;
  slug?: string; // 租户标识
  plan?: TenantType; // 租户类型
  status?: TenantStatus;
  description?: string;
}

// 租户列表响应
export interface TenantListResponse {
  tenants: Tenant[];
  total_count: number;
  has_next_page: boolean;
}

// 租户成员列表响应
export interface TenantMemberListResponse {
  items: TenantMember[];
  total: number;
}

/**
 * 获取租户列表
 */
export const getTenants = async (params?: {
  page?: number;
  size?: number;
  search?: string;
  status?: TenantStatus;
}): Promise<Tenant[]> => {
  try {
    const response = await apiGet<TenantListResponse>(
      '/v1/admin/tenants',
      params
    );
    return response.tenants || [];
  } catch (error) {
    console.error('获取租户列表失败:', error);
    // 返回模拟数据作为fallback
    return getMockTenants();
  }
};

/**
 * 获取租户详情
 */
export const getTenantById = async (id: string): Promise<Tenant> => {
  return apiGet<Tenant>(`/v1/admin/tenants/${id}`);
};

/**
 * 获取当前登录用户所属租户详情
 */
export const getCurrentTenant = async (): Promise<Tenant> => {
  return apiGet<Tenant>('/v1/tenants/current');
};

/**
 * 创建租户
 */
export const createTenant = async (
  data: CreateTenantRequest
): Promise<Tenant> => {
  return apiPost<Tenant>('/v1/admin/tenants', {
    name: data.name,
    slug: data.slug,
    plan: data.plan,
    status: data.status,
    description: data.description,
  });
};

/**
 * 更新租户
 */
export const updateTenant = async (
  id: string,
  data: UpdateTenantRequest
): Promise<Tenant> => {
  return apiPut<Tenant>(`/v1/admin/tenants/${id}`, {
    name: data.name,
    slug: data.slug,
    plan: data.plan,
    status: data.status,
    description: data.description,
  });
};

/**
 * 删除租户
 */
export const deleteTenant = async (id: string): Promise<void> => {
  return apiDelete<void>(`/v1/admin/tenants/${id}`);
};

/**
 * 获取默认租户（租户名称为'默认租户'）
 */
export const getDefaultTenant = async (): Promise<Tenant | null> => {
  try {
    console.log('🏢 获取默认租户信息...');
    const tenants = await getTenants();
    console.log('📋 租户列表:', tenants);

    // 查找租户名称为'默认租户'的租户
    const defaultTenant = tenants.find((tenant) => tenant.name === '默认租户');

    if (defaultTenant) {
      console.log('✅ 找到默认租户:', defaultTenant);
      return defaultTenant;
    } else {
      console.warn('⚠️ 未找到名称为"默认租户"的租户，返回第一个租户');
      return tenants.length > 0 ? tenants[0] : null;
    }
  } catch (error) {
    console.error('❌ 获取默认租户失败:', error);
    return null;
  }
};

/**
 * 获取默认租户ID
 */
export const getDefaultTenantId = async (): Promise<string | null> => {
  const defaultTenant = await getDefaultTenant();
  return defaultTenant?.id || null;
};

/**
 * 模拟租户数据
 */
function getMockTenants(): Tenant[] {
  return [
    {
      id: 'tenant_001',
      name: '默认租户',
      slug: 'default',
      plan: TenantType.FREE,
      status: TenantStatus.ACTIVE,
      member_count: 5,
      description: '默认租户',
      created_at: '2024-01-01T00:00:00Z',
    },
    {
      id: 'tenant_002',
      name: '演示租户',
      slug: 'demo',
      plan: TenantType.PRO,
      status: TenantStatus.ACTIVE,
      member_count: 10,
      description: '演示租户',
      created_at: '2024-01-15T00:00:00Z',
    },
    {
      id: 'tenant_003',
      name: '测试租户',
      slug: 'test',
      plan: TenantType.ENTERPRISE,
      status: TenantStatus.INACTIVE,
      member_count: 20,
      description: '测试租户',
      created_at: '2024-02-01T00:00:00Z',
    },
  ];
}
